<script lang="ts" setup>
import AppLoadingPlaceholder from "@/components/AppLoadingPlaceholder.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseCardHeading from "@/components/BaseCardHeading.vue";
import BaseContent from "@/components/BaseContent.vue";
import BaseLwfAnimation from "@/components/BaseLwfAnimation.vue";
import BasePageHeading from "@/components/BasePageHeading.vue";
import BaseTeam from "@/components/BaseTeam.vue";
import CharacterAcquirements from "@/components/CharacterAcquirements.vue";
import CharacterActions from "@/components/CharacterActions.vue";
import CharacterActiveSkill from "@/components/CharacterActiveSkill.vue";
import CharacterAwakeningPath from "@/components/CharacterAwakeningPath.vue";
import CharacterCategories from "@/components/CharacterCategories.vue";
import CharacterDates from "@/components/CharacterDates.vue";
import CharacterEzaPotential from "@/components/CharacterEzaPotential.vue";
import CharacterLeaderSkill from "@/components/CharacterLeaderSkill.vue";
import CharacterLinkSkills from "@/components/CharacterLinkSkills.vue";
import CharacterPassiveSkill from "@/components/CharacterPassiveSkill.vue";
import CharacterSkillOrbs from "@/components/CharacterSkillOrbs.vue";
import CharacterSkillVoting from "@/components/CharacterSkillVoting.vue";
import CharacterStandbySkill from "@/components/CharacterStandbySkill.vue";
import CharacterStats from "@/components/CharacterStats.vue";
import CharacterSuperAtk from "@/components/CharacterSuperAtk.vue";
import CharacterSuperAtkFodderCharacters from "@/components/CharacterSuperAtkFodderCharacters.vue";
import CharacterSupportMemories from "@/components/CharacterSupportMemories.vue";
import CharacterThumbnail from "@/components/CharacterThumbnail.vue";
import DokkanCharacterDetails from "@/components/DokkanCharacterDetails.vue";
import TheCommentsCard from "@/components/TheCommentsCard.vue";
import TheLayout from "@/components/TheLayout.vue";
import TheSocialSharingBlock from "@/components/TheSocialSharingBlock.vue";
import useCharacter from "@/composables/character";
import CharacterLinkPartners from "@/pages/Character/partials/CharacterLinkPartners.vue";
import { PaginatedCommentData } from "@/types";
import { Character } from "@/types/character";
import { router, usePage } from "@inertiajs/vue3";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, onMounted, ref } from "vue";

const page = usePage();

const props = defineProps<{
  character: Character;
  link_partners?: Character[];
  team?: Character[];
  comments?: PaginatedCommentData;
  schema: object;
}>();

const breakpoints = useBreakpoints(breakpointsTailwind);

onMounted(() => {
  router.reload({ only: ["team"] });
});

const potentialUnlocked = ref<boolean | null>(
  props.character.hidden_potential !== null,
);
const eza = ref(props.character.extreme_z_awakenings?.length > 0);
const hasEza = ref(props.character.extreme_z_awakenings?.length > 0);
const hasSeza = ref<boolean | null>(
  props.character.awakenings[props.character.awakenings.length - 1]
    ?.eza_type === 2,
);
const seza = computed<boolean>(() => eza.value && hasSeza.value);
const transformation = ref(
  props.character.id > 4000000 &&
    props.character.reversible_exchange_character_id !== null,
);
const rarityText = useCharacter(props.character).rarityText;

const typeFormatted = computed<string>(() => {
  let type =
    props.character.rarity > 3
      ? props.character.awakening_type === 1
        ? "Super "
        : "Extreme "
      : "";

  switch (props.character.type) {
    case 0:
      type += "AGL";
      break;
    case 1:
      type += "TEQ";
      break;
    case 2:
      type += "INT";
      break;
    case 3:
      type += "STR";
      break;
    case 4:
      type += "PHY";
      break;
  }

  return type;
});

const pageTitle = computed<string>(() => {
  return `${props.character.name} ${
    props.character.leader_skill !== null
      ? `- ${props.character.leader_skill.name}`
      : ""
  }`;
});

const pageDescription = computed<string>(() => {
  return `Details &amp; link partners for DRAGON BALL Z Dokkan Battle character ${
    hasEza.value ? (hasSeza.value ? "Super " : "") + "Extreme Z-Awakened " : ""
  }${rarityText.value.toUpperCase()} ${typeFormatted.value} ${props.character.name}.${
    props.character.leader_skill !== null
      ? " | " + props.character.leader_skill.description.replace(/"/g, "'")
      : ""
  }`;
});

const linkPartners = ref<HTMLElement | null>(null);

const scrollToLinkPartners = () => {
  linkPartners?.value.scrollToTop();
};
</script>

<template>
  <TheLayout
    :class="{
      '!from-blue-600/10': eza,
      '!from-fuchsia-600/10': seza,
      '!to-red-600/10': transformation,
    }"
    :description="pageDescription"
    :head-image="`${page.props.app.cdn_url}/assets/en/character/card/${character.thumbnail_id}/card_${character.thumbnail_id}_cutin.png`"
    :heading-href="route('characters.index')"
    :schema="schema"
    :show-header-ad="false"
    :title="pageTitle"
    class="bg-gradient-to-tr from-transparent via-transparent to-transparent"
    heading="Characters"
  >
    <!-- Heading -->
    <div
      class="relative flex w-full justify-between gap-8 xl:-my-4 xl:h-[266px]"
    >
      <!-- Cut-in (!sm-lg) -->
      <div
        class="relative -z-10 -my-4 block max-w-full sm:right-0 sm:my-0 sm:hidden xl:absolute xl:float-right xl:block"
      >
        <img
          :alt="character.name"
          :src="`${page.props.app.cdn_url}/assets/en/character/card/${character.thumbnail_id}/card_${character.thumbnail_id}_cutin.png`"
          class="object-none"
        />
      </div>

      <!-- Thumbnail, Name & Actions (>=sm) -->
      <div
        class="hidden items-center gap-4 px-4 opacity-100 sm:flex sm:px-0 xl:w-1/2"
      >
        <CharacterThumbnail
          :base-character-id="character.id"
          :character="character"
          :potential-unlocked="potentialUnlocked"
          :transformation="transformation"
          class="mx-2 shrink-0"
          @toggle-hidden-potential="potentialUnlocked = !potentialUnlocked"
        />

        <div class="flex flex-col gap-2">
          <span
            v-if="character.leader_skill !== null"
            class="-mb-3 hidden bg-gradient-to-tr from-green-200 to-green-100 bg-clip-text font-subheading text-xl font-bold text-transparent drop-shadow sm:inline"
            >{{ character.leader_skill.name }}
          </span>

          <BasePageHeading>
            <span v-if="character.leader_skill !== null" class="hidden"
              >[{{ character.leader_skill.name }}]</span
            >
            {{ character.name }}
          </BasePageHeading>

          <CharacterActions v-model:eza="eza" :character="character" />
        </div>
      </div>

      <TheSocialSharingBlock
        :hide="
          breakpoints.smaller('sm').value
            ? ['facebook', 'messenger', 'whatsapp', 'reddit']
            : []
        "
        :rows="
          breakpoints.smaller('sm').value
            ? 1
            : breakpoints.smaller('lg').value
              ? 2
              : 1
        "
        :text="pageTitle"
        class="absolute right-0 rounded-r-none sm:relative sm:mt-0 sm:rounded-r-md xl:mt-4"
      />
    </div>

    <!-- Content -->
    <div class="flex flex-col gap-4 lg:flex-row">
      <!-- Left Column -->
      <div class="flex flex-col gap-4 lg:w-2/5 xl:w-1/3 2xl:w-1/4">
        <!-- Details (<sm) -->
        <div v-if="breakpoints.smaller('sm').value">
          <BaseCard>
            <div class="flex gap-4">
              <div class="flex grow flex-col gap-2">
                <BaseCardHeading>
                  {{ character.name }}

                  <template v-if="character.leader_skill !== null" #subheading
                    >{{ character.leader_skill.name }}
                  </template>
                </BaseCardHeading>

                <DokkanCharacterDetails :character="character" />

                <CharacterActions v-model:eza="eza" :character="character" />
              </div>

              <CharacterThumbnail
                :base-character-id="character.id"
                :character="character"
                :potential-unlocked="potentialUnlocked"
                :transformation="transformation"
                class="mr-2 mt-2 shrink-0"
                @toggle-hidden-potential="
                  potentialUnlocked = !potentialUnlocked
                "
              />
            </div>

            <CharacterStats
              v-if="character.cost > 1"
              :character="character"
              :cost="true"
              :eza="eza"
              :ki="true"
              :lv="true"
              :potential-unlocked="potentialUnlocked"
              class="sm:grid-cols-6 lg:grid-cols-3"
            />

            <CharacterDates v-if="character.cost > 1" :character="character" />
          </BaseCard>
        </div>

        <!-- Details (>=sm) -->
        <div
          v-if="character.cost > 1 && breakpoints.greaterOrEqual('sm').value"
        >
          <BaseCard>
            <template #heading>Details</template>

            <CharacterStats
              :character="character"
              :cost="true"
              :eza="eza"
              :ki="true"
              :lv="true"
              :potential-unlocked="potentialUnlocked"
              class="sm:!flex lg:!grid"
            />

            <CharacterDates :character="character" />

            <div v-if="character.drop_area">
              <BaseContent :header="`Drops From ${character.drop_area.type}`">
                {{ character.drop_area.name }}
              </BaseContent>
            </div>
          </BaseCard>
        </div>

        <BaseCard>
          <BaseLwfAnimation
            v-if="character.rarity >= 5"
            :href="`${page.props.app.cdn_url}/assets/en/character/card_bg/${character.thumbnail_id}/card_${character.thumbnail_id}.lwf`"
            height="568"
            width="426"
          />
          <div
            v-if="character.rarity < 5"
            class="relative mx-auto aspect-[3/4] w-full"
          >
            <img
              ref="characterBg"
              :src="`${page.props.app.cdn_url}/assets/en/character/card/${character.thumbnail_id}/card_${character.thumbnail_id}_bg.png`"
              alt=""
              class="absolute inset-0 w-full object-cover"
            />
            <img
              :alt="character.name"
              :src="`${page.props.app.cdn_url}/assets/en/character/card/${character.thumbnail_id}/card_${character.thumbnail_id}_character.png`"
              class="absolute inset-0 w-full object-cover"
            />
            <img
              :src="`${page.props.app.cdn_url}/assets/en/character/card/${character.thumbnail_id}/card_${character.thumbnail_id}_effect.png`"
              alt=""
              class="absolute inset-0 w-full object-cover"
            />
          </div>
        </BaseCard>

        <CharacterAcquirements
          v-if="
            character.drop_reward_stages?.length > 0 ||
            character.treasure_exchanges?.length > 0
          "
          :character="character"
        />

        <CharacterSuperAtkFodderCharacters
          v-if="character.super_atk_fodder_characters.length > 0"
          :characters="character.super_atk_fodder_characters"
        />

        <CharacterSkillVoting
          v-if="character.rarity >= 4"
          :character="character.base_character ?? character"
          :has-eza="hasEza"
          :has-seza="hasSeza"
        />

        <CharacterLinkSkills
          v-if="
            character.link_skills[0] !== null &&
            breakpoints.greaterOrEqual('lg').value
          "
          :link-skills="character.link_skills"
          class="p-4"
          @scroll-to-link-partners="scrollToLinkPartners"
        />

        <CharacterCategories
          v-if="
            character.categories.length > 0 &&
            breakpoints.greaterOrEqual('lg').value
          "
          :categories="character.categories"
        />

        <CharacterSkillOrbs
          v-if="
            character.skill_orbs &&
            character.skill_orbs.length > 0 &&
            breakpoints.greaterOrEqual('lg').value
          "
          :skill-orbs="character.skill_orbs"
        />
        <BaseCard v-else-if="breakpoints.greaterOrEqual('lg').value">
          <template #heading>Exclusive Skill Orbs</template>
          No exclusive Skill Orbs available.
        </BaseCard>

        <CharacterAwakeningPath
          v-if="
            character.awakening_path !== null &&
            breakpoints.greaterOrEqual('lg').value
          "
          key="awakening_path.lg"
          :awakening-path="character.awakening_path"
        />

        <CharacterEzaPotential
          v-if="
            !hasEza &&
            character.rarity === 4 &&
            character.cost > 1 &&
            breakpoints.greaterOrEqual('lg').value
          "
          :character="character"
          :potential-unlocked="potentialUnlocked"
        />
      </div>

      <!-- Right Column -->
      <div class="flex flex-col gap-4 lg:w-2/3 xl:w-3/4">
        <CharacterLeaderSkill
          v-if="character.leader_skill"
          :character="character"
          :eza="eza"
        />

        <CharacterSuperAtk
          v-if="
            character.super_atks?.[0] && character.super_atks[0].name !== 'None'
          "
          :eza="eza"
          :super-atk-lv-max="
            eza
              ? character.extreme_z_awakenings[
                  character.extreme_z_awakenings.length - 1
                ].super_atk_lv_max
              : character.super_atk_lv_max
          "
          :super-atks="character.super_atks"
        />

        <CharacterPassiveSkill
          v-if="character.passive_skill"
          :character="character"
          :eza="eza"
        />

        <CharacterActiveSkill
          v-if="character.active_skill"
          :active-skill="character.active_skill"
        />

        <CharacterStandbySkill
          v-if="character.standby_skill"
          :standby-skill="character.standby_skill"
        />

        <CharacterLinkSkills
          v-if="
            character.link_skills[0] !== null && breakpoints.smaller('lg').value
          "
          :link-skills="character.link_skills"
          class="p-4"
          @scroll-to-link-partners="scrollToLinkPartners"
        />

        <CharacterLinkPartners
          v-if="character.link_skills[0] !== null"
          ref="linkPartners"
          :categories="character.categories"
          :link-partners="link_partners"
          :link-skills="character.link_skills"
        />

        <CharacterCategories
          v-if="
            character.categories.length > 0 && breakpoints.smaller('lg').value
          "
          :categories="character.categories"
        />

        <CharacterSkillOrbs
          v-if="
            character.skill_orbs &&
            character.skill_orbs.length > 0 &&
            breakpoints.smaller('lg').value
          "
          :skill-orbs="character.skill_orbs"
        />
        <BaseCard v-else-if="breakpoints.smaller('lg').value">
          <template #heading>Exclusive Skill Orbs</template>
          No exclusive Skill Orbs available.
        </BaseCard>

        <CharacterAwakeningPath
          v-if="
            character.awakening_path !== null && breakpoints.smaller('lg').value
          "
          key="awakening_path.sm"
          :awakening-path="character.awakening_path"
        />

        <CharacterEzaPotential
          v-if="
            !hasEza &&
            character.rarity === 4 &&
            character.cost > 1 &&
            breakpoints.smaller('lg').value
          "
          :character="character"
          :potential-unlocked="potentialUnlocked"
        />

        <CharacterSupportMemories
          :support-memories="character.support_memories"
        />

        <template v-if="character.rarity >= 4 && character.cost > 1">
          <BaseTeam
            v-if="team"
            :characters="team"
            :footer="{
              content: 'Edit Team Formation',
              href: route('teams.create', {
                characters: team.map((c) => c.id).join(','),
              }),
            }"
            hide-slot-numbers
            show-leader-skill
            title="Example Team Formation"
          />
          <BaseCard v-else>
            <template #heading>Example Team Formation</template>
            <AppLoadingPlaceholder
              >Creating example team formation...
            </AppLoadingPlaceholder>
          </BaseCard>
        </template>
      </div>
    </div>

    <TheCommentsCard
      :commentable-id="character.id"
      :comments="comments"
      commentable-type="App\Models\Dokkan\Card"
    />
  </TheLayout>
</template>

<style scoped></style>
